import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Coins, Wallet, TrendingUp, TrendingDown, ArrowDown, ArrowUp, Percent, Settings2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Role-aware commission dashboard. Sales Executive sees own view;
 *  Admin/SA/BM see an executives list with drill-down + debit ability. */
export default function CommissionDashboard() {
  const { user } = useAuth();
  const isSE = user?.role === 'Sales Executive';
  const isManager = ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role);
  const canConfigure = ['Super Admin', 'Admin'].includes(user?.role);

  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [execs, setExecs] = useState([]);
  const [selectedExec, setSelectedExec] = useState(null); // for admin drill
  const [debitOpen, setDebitOpen] = useState(false);
  const [debit, setDebit] = useState({
    sales_executive_id: '',
    sales_executive_name: '',
    amount: '',
    description: '',
    payment_method: 'Cash',
    payment_date: new Date().toISOString().slice(0, 10),
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ sales_executive_id: '', sales_executive_name: '', customer: null, custom_amount: '', description: '' });
  const [manualQuery, setManualQuery] = useState('');
  const [manualResults, setManualResults] = useState([]);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState({ user_id: '', commission_percent: '', show_commission: false });

  const loadSE = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API_URL}/api/commission/summary/me`, { withCredentials: true }),
        axios.get(`${API_URL}/api/commission/ledger/me`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setLedger(l.data || []);
    } catch (e) {
      if (e.response?.status === 403) setSummary({ disabled: true });
      else toast.error(e.response?.data?.detail || 'Failed to load commission');
    }
  };

  const loadAdmin = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/commission/executives`, { withCredentials: true });
      setExecs(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const loadDrill = async (uid) => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API_URL}/api/commission/summary/${uid}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/commission/ledger/${uid}`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setLedger(l.data || []);
      setSelectedExec(uid);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  useEffect(() => {
    if (isSE) loadSE();
    else if (isManager) loadAdmin();
  }, [isSE, isManager]);

  const submitDebit = async () => {
    try {
      const amount = parseFloat(debit.amount);
      if (!(amount > 0)) { toast.error('Amount must be > 0'); return; }
      const { data } = await axios.post(`${API_URL}/api/commission/debit`, {
        sales_executive_id: debit.sales_executive_id,
        amount,
        description: debit.description.trim() || 'Commission payout',
        payment_method: debit.payment_method || 'Cash',
        payment_date: debit.payment_date ? `${debit.payment_date}T00:00:00Z` : null,
      }, { withCredentials: true });
      toast.success(`Debited PKR ${amount.toLocaleString()} — ${data.receipt_number}`);
      if (data.receipt_pdf) {
        const w = window.open('', '_blank');
        if (w) w.document.write(`<iframe src="${data.receipt_pdf}" style="width:100%;height:100%;border:0" onload="setTimeout(()=>this.contentWindow.print(),400)"></iframe>`);
      }
      setDebitOpen(false);
      setDebit({
        sales_executive_id: '', sales_executive_name: '',
        amount: '', description: '',
        payment_method: 'Cash',
        payment_date: new Date().toISOString().slice(0, 10),
      });
      if (selectedExec) loadDrill(selectedExec);
      loadAdmin();
    } catch (e) { toast.error(e.response?.data?.detail || 'Debit failed'); }
  };

  const openDebitFor = (exec) => {
    setDebit({
      sales_executive_id: exec.id,
      sales_executive_name: exec.name,
      amount: '', description: '',
      payment_method: 'Cash',
      payment_date: new Date().toISOString().slice(0, 10),
    });
    setDebitOpen(true);
  };

  const openManualFor = (exec) => {
    setManual({ sales_executive_id: exec.id, sales_executive_name: exec.name,
                customer: null, custom_amount: '', description: '' });
    setManualQuery('');
    setManualResults([]);
    setManualOpen(true);
  };

  const submitCfg = async () => {
    try {
      await axios.put(`${API_URL}/api/users/${cfg.user_id}/commission-settings`, {
        commission_percent: parseFloat(cfg.commission_percent || 0),
        show_commission: cfg.show_commission,
      }, { withCredentials: true });
      toast.success('Commission settings updated');
      setCfgOpen(false);
      loadAdmin();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  // ============================== SE view (own dashboard)
  if (isSE) {
    if (summary?.disabled) {
      return <div className="text-slate-400 text-center py-12" data-testid="commission-disabled">
        Your commission dashboard is currently hidden. Please contact your Admin.
      </div>;
    }
    return renderDashboard({
      title: `${user.name} — My Commission`,
      summary, ledger, showBackToList: false,
      hideDetails: true,   // SE cannot see sale/profit/% columns per policy
    });
  }

  // ============================== Manager view
  if (!isManager) return <div className="text-slate-400 text-center py-12">Not authorized</div>;

  // Delete a ledger row (credit or debit). Admins only. Confirms in the
  // browser, hits DELETE /api/commission/ledger/{id}?confirm=true, then
  // refreshes the drill-down or ledger.
  const canDelete = ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role);
  const handleDeleteLedger = async (entry) => {
    if (!canDelete) return;
    const label = entry.entry_type === 'credit' ? 'commission credit' : 'commission debit';
    const ok = window.confirm(
      `Delete this ${label} of PKR ${Number(entry.amount || 0).toLocaleString()}?\n\n`
      + `This will remove the entry and recompute ${entry.sales_executive_name || 'the SE'}'s balance.\n\n`
      + `This action cannot be undone.`
    );
    if (!ok) return;
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/commission/ledger/${entry.id}?confirm=true`,
        { withCredentials: true }
      );
      toast.success(`${label[0].toUpperCase() + label.slice(1)} deleted · new balance PKR ${Number(data.new_balance).toLocaleString()}`);
      // Refresh whichever view is showing
      if (selectedExec) {
        await loadDrill(selectedExec);
      }
      await loadAdmin();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ============================== Manager view render helpers ==============================
  const seRowForDebit = execs.find((e) => e.id === debit.sales_executive_id);
  const availBalance = seRowForDebit?.balance ?? (summary?.balance ?? 0);

  const seRowForManual = execs.find((e) => e.id === manual.sales_executive_id);
  const manualPct = seRowForManual?.commission_percent || 0;
  const manualBc = manual.customer?.business_charge || 0;
  const manualAuto = manualBc > 0 && manualPct > 0 ? Math.round(manualBc * manualPct) / 100 : 0;

  const debitDialogJsx = (
    <Dialog open={debitOpen} onOpenChange={setDebitOpen}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="debit-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-400">Post Commission Debit</DialogTitle>
          <p className="text-slate-400 text-xs mt-1">
            Withdraw commission from
            <span className="text-white font-medium ml-1">{debit.sales_executive_name || 'Sales Executive'}</span>
            {' '}· Available <span className="text-amber-400">PKR {(availBalance || 0).toLocaleString()}</span>
          </p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-400 text-xs">Amount (PKR) *</Label>
            <Input type="number" step="0.01" min="0" value={debit.amount}
              onChange={(e) => setDebit({ ...debit, amount: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white" data-testid="debit-amount" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-400 text-xs">Date *</Label>
              <Input type="date" value={debit.payment_date}
                onChange={(e) => setDebit({ ...debit, payment_date: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" data-testid="debit-date" />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Payment Method *</Label>
              <select value={debit.payment_method}
                onChange={(e) => setDebit({ ...debit, payment_method: e.target.value })}
                data-testid="debit-method"
                className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-white text-sm">
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Notes (optional)</Label>
            <Input value={debit.description} onChange={(e) => setDebit({ ...debit, description: e.target.value })}
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g. Cheque #12345, cash payout" data-testid="debit-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setDebitOpen(false)}>Cancel</Button>
          <Button variant="destructive" onClick={submitDebit} data-testid="debit-submit">Debit &amp; Print Slip</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const manualDialogJsx = (
    <Dialog open={manualOpen} onOpenChange={setManualOpen}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid="manual-credit-dialog">
        <DialogHeader>
          <DialogTitle className="text-emerald-400">Post Manual Commission Credit</DialogTitle>
          {manual.sales_executive_name && (
            <p className="text-slate-400 text-xs mt-1">
              Crediting <span className="text-white font-medium">{manual.sales_executive_name}</span>
              {manualPct ? <span> · {manualPct}% commission rate</span> : null}
            </p>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-400 text-xs">Customer *</Label>
            {manual.customer ? (
              <div className="bg-slate-800 border border-emerald-500/40 rounded-sm p-2 flex items-center justify-between">
                <div>
                  <div className="text-white text-sm">{manual.customer.name}</div>
                  <div className="text-slate-400 text-xs">
                    {manual.customer.account_number}
                    {manualBc > 0 && (<>{' · '}Agreement Profit: <span className="text-amber-500">PKR {manualBc.toLocaleString()}</span></>)}
                  </div>
                </div>
                <button type="button" className="text-slate-400 hover:text-red-400 text-xs"
                  onClick={() => setManual((m) => ({ ...m, customer: null }))}>× Change</button>
              </div>
            ) : (
              <>
                <Input value={manualQuery} placeholder="Search by name / account / CNIC / mobile"
                  onChange={(e) => runManualSearch(e.target.value)}
                  data-testid="manual-customer-search"
                  autoComplete="off"
                  className="bg-slate-800 border-slate-700 text-white" />
                {manualResults.length > 0 && (
                  <div className="mt-1 max-h-40 overflow-y-auto border border-slate-700 rounded-sm bg-slate-800">
                    {manualResults.map((r) => (
                      <button key={r.id} type="button"
                        onClick={() => setManual((m) => ({ ...m, customer: r }))}
                        data-testid={`manual-customer-pick-${r.id}`}
                        className="w-full text-left px-2 py-1 hover:bg-slate-700 text-xs border-b border-slate-700">
                        <div className="text-white">{r.name} <span className="text-slate-500">— {r.account_number}</span></div>
                        <div className="text-slate-400">
                          {r.mobile}
                          {r.business_charge > 0 && (<> · Profit <span className="text-amber-500">PKR {r.business_charge.toLocaleString()}</span></>)}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {manualQuery && manualQuery.length >= 2 && manualResults.length === 0 && (
                  <p className="text-slate-500 text-xs mt-1">No customers matched "{manualQuery}"</p>
                )}
              </>
            )}
          </div>
          <div>
            <Label className="text-slate-400 text-xs flex items-center justify-between">
              <span>Amount (PKR) *</span>
              {manualAuto > 0 && (
                <button type="button"
                  className="text-emerald-400 hover:text-emerald-300 text-xs"
                  onClick={() => setManual((m) => ({ ...m, custom_amount: String(manualAuto) }))}
                  data-testid="manual-use-auto">
                  Use auto (PKR {manualAuto.toLocaleString()})
                </button>
              )}
            </Label>
            <Input type="number" step="0.01" min="0" value={manual.custom_amount}
              onChange={(e) => setManual((m) => ({ ...m, custom_amount: e.target.value }))}
              data-testid="manual-custom-amount"
              autoComplete="off"
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="Enter commission amount" />
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Description / Reference (optional)</Label>
            <Input value={manual.description}
              onChange={(e) => setManual((m) => ({ ...m, description: e.target.value }))}
              data-testid="manual-description"
              autoComplete="off"
              className="bg-slate-800 border-slate-700 text-white"
              placeholder="e.g. Q1 bonus, extra recognition" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitManualCredit}
            disabled={!manual.customer || !manual.custom_amount || Number(manual.custom_amount) <= 0}
            data-testid="manual-submit">
            Credit & Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (selectedExec && summary) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => { setSelectedExec(null); setSummary(null); setLedger([]); }} data-testid="back-to-execs">← Back to Executives</Button>
        {renderDashboard({
          title: `${summary.user_name} — Commission Ledger`,
          summary, ledger, showBackToList: true,
          onDeleteEntry: handleDeleteLedger,
          extra: (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => openManualFor({ id: selectedExec, name: summary?.user_name })}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="open-manual-credit">
                <ArrowUp className="h-4 w-4 mr-2" /> Post Manual Credit
              </Button>
              <Button onClick={() => openDebitFor({ id: selectedExec, name: summary?.user_name })}
                className="bg-red-600 hover:bg-red-700" data-testid="open-debit">
                <ArrowDown className="h-4 w-4 mr-2" /> Post Debit
              </Button>
            </div>
          )
        })}
        {debitDialogJsx}
        {manualDialogJsx}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commission-execs-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Sales Executive Commissions</h1>
          <p className="text-slate-400 text-sm">Configure commission %, view ledgers, post debits.</p>
        </div>
      </div>
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white text-base">Executives ({execs.length})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow className="border-slate-800">
              <TableHead className="text-slate-400">Name</TableHead>
              <TableHead className="text-slate-400">Commission %</TableHead>
              <TableHead className="text-slate-400">Credit</TableHead>
              <TableHead className="text-slate-400">Debit</TableHead>
              <TableHead className="text-slate-400">Balance</TableHead>
              <TableHead className="text-slate-400">Show?</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {execs.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-slate-500 text-center">No Sales Executives found.</TableCell></TableRow>
              )}
              {execs.map((e) => (
                <TableRow key={e.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`exec-row-${e.id}`}>
                  <TableCell className="text-white">{e.name}</TableCell>
                  <TableCell className="text-amber-400">{e.commission_percent}%</TableCell>
                  <TableCell className="text-emerald-400">PKR {e.total_credit.toLocaleString()}</TableCell>
                  <TableCell className="text-red-400">PKR {e.total_debit.toLocaleString()}</TableCell>
                  <TableCell className="text-white font-semibold">PKR {e.balance.toLocaleString()}</TableCell>
                  <TableCell className="text-xs">
                    <span className={e.show_commission ? 'text-emerald-400' : 'text-slate-500'}>{e.show_commission ? 'ON' : 'OFF'}</span>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => loadDrill(e.id)} data-testid={`drill-${e.id}`}>View</Button>
                    <Button size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openManualFor(e)} data-testid={`credit-${e.id}`}>
                      <ArrowUp className="h-3 w-3 mr-1" /> Credit
                    </Button>
                    <Button size="sm" className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => openDebitFor(e)} data-testid={`debit-${e.id}`}
                      disabled={(e.balance || 0) <= 0}>
                      <ArrowDown className="h-3 w-3 mr-1" /> Debit
                    </Button>
                    {canConfigure && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid={`configure-${e.id}`}
                        onClick={() => {
                          setCfg({ user_id: e.id, commission_percent: e.commission_percent, show_commission: e.show_commission });
                          setCfgOpen(true);
                        }}><Settings2 className="h-3 w-3 mr-1" /> Configure</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Configure commission dialog */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="cfg-dialog">
          <DialogHeader><DialogTitle>Configure Commission</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-400 text-xs">Commission %</Label>
              <Input type="number" step="0.01" min="0" max="100" value={cfg.commission_percent}
                onChange={(e) => setCfg({ ...cfg, commission_percent: e.target.value })}
                className="bg-slate-800 border-slate-700 text-white" data-testid="cfg-percent" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" checked={cfg.show_commission}
                onChange={(e) => setCfg({ ...cfg, show_commission: e.target.checked })}
                className="accent-emerald-500" data-testid="cfg-show" />
              Allow this SE to view their commission dashboard
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCfgOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitCfg} data-testid="cfg-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {debitDialogJsx}
      {manualDialogJsx}
    </div>
  );

  async function runManualSearch(q) {
    setManualQuery(q);
    if (!q || q.length < 2) { setManualResults([]); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/commission/customer-search?q=${encodeURIComponent(q)}`,
        { withCredentials: true });
      setManualResults(data || []);
    } catch (e) { /* ignore */ }
  }

  async function submitManualCredit() {
    if (!manual.sales_executive_id) { toast.error('Sales Executive not selected'); return; }
    if (!manual.customer) { toast.error('Pick a customer'); return; }
    const amt = Number(manual.custom_amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      const { data } = await axios.post(`${API_URL}/api/commission/manual-credit`, {
        sales_executive_id: manual.sales_executive_id,
        customer_id: manual.customer.id,
        custom_amount: amt,
        description: manual.description || null,
      }, { withCredentials: true });
      toast.success(`Credited PKR ${data.amount?.toLocaleString()} — ${data.receipt_number}`);
      if (data.receipt_pdf) {
        const w = window.open('', '_blank');
        if (w) w.document.write(`<iframe src="${data.receipt_pdf}" style="width:100%;height:100%;border:0" onload="setTimeout(()=>this.contentWindow.print(),400)"></iframe>`);
      }
      setManualOpen(false);
      setManual({ sales_executive_id: '', sales_executive_name: '', customer: null, custom_amount: '', description: '' });
      setManualResults([]); setManualQuery('');
      if (selectedExec) loadDrill(selectedExec);
      loadAdmin();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Credit failed');
    }
  }


}

// ------------------------------------------------ shared dashboard renderer
function renderDashboard({ title, summary, ledger, extra, hideDetails = false, onDeleteEntry = null }) {
  const cards = summary ? [
    { label: 'Total Commission (Credit)', value: summary.total_credit, icon: TrendingUp, color: 'text-emerald-400', testId: 'summary-total-credit' },
    { label: 'Total Debit / Paid', value: summary.total_debit, icon: TrendingDown, color: 'text-red-400', testId: 'summary-total-debit' },
    { label: 'Available Balance', value: summary.balance, icon: Wallet, color: 'text-amber-400', testId: 'summary-balance' },
    ...(hideDetails ? [] : [{ label: 'Commission %', value: `${summary.commission_percent}%`, icon: Percent, color: 'text-blue-400', testId: 'summary-percent' }]),
  ] : [];
  return (
    <div className="space-y-6" data-testid="commission-dashboard">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white" data-testid="commission-title">{title}</h1>
        {extra}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="bg-slate-900 border-slate-800" data-testid={c.testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-1">
              <CardTitle className="text-slate-400 text-xs uppercase tracking-wide">{c.label}</CardTitle>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${c.color}`}>{typeof c.value === 'number' ? `PKR ${c.value.toLocaleString()}` : c.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-white text-base flex items-center"><Coins className="h-4 w-4 mr-2 text-amber-500" /> Transactions ({ledger.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-slate-800">
                <TableHead className="text-slate-400 text-xs">Date</TableHead>
                <TableHead className="text-slate-400 text-xs">Type</TableHead>
                <TableHead className="text-slate-400 text-xs">Customer</TableHead>
                <TableHead className="text-slate-400 text-xs">Agreement</TableHead>
                {!hideDetails && <TableHead className="text-slate-400 text-xs text-right">Sale</TableHead>}
                {!hideDetails && <TableHead className="text-slate-400 text-xs text-right">Profit</TableHead>}
                {!hideDetails && <TableHead className="text-slate-400 text-xs text-right">%</TableHead>}
                <TableHead className="text-slate-400 text-xs text-right">Commission</TableHead>
                <TableHead className="text-slate-400 text-xs">Method</TableHead>
                <TableHead className="text-slate-400 text-xs">Description</TableHead>
                <TableHead className="text-slate-400 text-xs">Authorized</TableHead>
                <TableHead className="text-slate-400 text-xs text-right">Balance</TableHead>
                {onDeleteEntry && <TableHead className="text-slate-400 text-xs text-center">Actions</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {ledger.length === 0 && (
                  <TableRow><TableCell colSpan={hideDetails ? 9 : 12} className="text-slate-500 text-center">No transactions yet.</TableCell></TableRow>
                )}
                {ledger.map((e) => (
                  <TableRow key={e.id} className="border-slate-800" data-testid={`ledger-row-${e.id}`}>
                    <TableCell className="text-slate-300 text-xs">{new Date(e.payment_date || e.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</TableCell>
                    <TableCell>
                      {e.entry_type === 'credit'
                        ? <span className="text-emerald-400 text-xs flex items-center"><ArrowUp className="h-3 w-3 mr-1" /> Credit</span>
                        : <span className="text-red-400 text-xs flex items-center"><ArrowDown className="h-3 w-3 mr-1" /> Debit</span>}
                    </TableCell>
                    <TableCell className="text-white text-xs">{e.customer_name || '—'}</TableCell>
                    <TableCell className="text-slate-300 text-xs font-mono">{e.agreement_number || '—'}</TableCell>
                    {!hideDetails && <TableCell className="text-slate-300 text-xs text-right">{e.sale_amount != null ? `PKR ${e.sale_amount.toLocaleString()}` : '—'}</TableCell>}
                    {!hideDetails && <TableCell className="text-slate-300 text-xs text-right">{e.business_charge != null ? `PKR ${e.business_charge.toLocaleString()}` : '—'}</TableCell>}
                    {!hideDetails && <TableCell className="text-slate-300 text-xs text-right">{e.commission_percent != null ? `${e.commission_percent}%` : '—'}</TableCell>}
                    <TableCell className={`text-xs text-right font-semibold ${e.entry_type === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>PKR {e.amount.toLocaleString()}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{e.payment_method || '—'}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{e.description}</TableCell>
                    <TableCell className="text-slate-400 text-xs">{e.authorized_by_name || '—'}</TableCell>
                    <TableCell className="text-white text-xs text-right font-semibold">PKR {e.balance_after.toLocaleString()}</TableCell>
                    {onDeleteEntry && (
                      <TableCell className="text-center">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => onDeleteEntry(e)}
                          className="h-7 w-7 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                          title={`Delete this ${e.entry_type}`}
                          data-testid={`delete-ledger-${e.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
